import React, { useEffect, useMemo, useState } from 'react';
import {
    Box,
    Button,
    TextField,
    Typography,
} from '@material-ui/core';
import { useFormik } from 'formik';
import { useTranslation } from 'react-i18next';
import { useSnackbar } from 'notistack';
import { EnchancedModal } from 'src/components/Modals';
import { SelectFilter } from 'src/components/Search/components';
import { mapStores } from 'src/components/Search/utils/mapStores';
import { AreaCodeTextField } from 'src/modules/forms/fields/AreaCodeTextField/AreaCodeTextField';
import { ECountryCode } from 'src/modules/forms/fields/AreaCodeTextField/components/AreaCodeSelect';
import useAuth from 'src/hooks/useAuth';
import useStore from 'src/hooks/useStore';
import { DEVELOPER_LEVEL_ACCESS } from 'src/components/access';
import { WHATSAPP_CHANNEL } from 'src/constants/conversations';
import { ModalTemplates } from 'src/modules/conversations/components/modalTemplates';
import type {
    ICurrentConversation,
    TCurrentTemplate,
} from 'src/modules/conversations/components/modalTemplates/types';
import { ConversationFactory } from 'src/modules/conversations/factories';
import { TEMPLATE_CONTEXTS_TYPES } from 'src/modules/conversations/constants/templates';
import { CURRENT_TEMPLATE_DEFAULT } from 'src/modules/conversations/constants/threads';
import { getContactWhatsAppPhone } from 'src/modules/contacts/helpers/getContactWhatsAppUrl';
import { getAccessibleStoresForUser } from '../../utils/storeTags';
import { getDocumentId } from 'src/utils/mongo/getDocumentId';
import type { TAreas } from 'src/constants/lead/leadTypes';
import {
    sendChannelTemplate,
    TSendChannelTemplateBody,
} from 'src/modules/conversations/api';

const OUTBOUND_CHANNELS = [
    {
        value: WHATSAPP_CHANNEL,
        labelKey: 'Chat.ChannelWhatsapp',
    },
] as const;

const OUTBOUND_CONVERSATION_ID_PREFIX = 'outbound-';

const buildOutboundConversationId = (
    storeId: string,
    area: string,
    phone: string
) => `${OUTBOUND_CONVERSATION_ID_PREFIX}${storeId}-${area}-${phone}`;

const isOutboundConversationId = (conversationId?: string) =>
    Boolean(
        conversationId?.startsWith(OUTBOUND_CONVERSATION_ID_PREFIX)
    );

type TSendOutboundMessageModalProps = {
    open: boolean;
    setOpen: (open: boolean) => void;
    onMessageSent?: () => void;
};

const getWhatsappAreas = (
    areas: any[] = [],
    userAreas: string[] = []
): any[] => {
    const withWhatsapp = areas.filter(
        (item) => item.isWspAvailable || item.whatsapp || item.phone
    );

    const byUser = withWhatsapp.filter((item) =>
        userAreas.includes(item.name)
    );

    return byUser.length ? byUser : withWhatsapp;
};

export const SendOutboundMessageModal = ({
    open,
    setOpen,
    onMessageSent,
}: TSendOutboundMessageModalProps) => {
    const { t } = useTranslation();
    const { enqueueSnackbar } = useSnackbar();
    const { user } = useAuth();
    const { store, stores, getStore, getStores } = useStore();

    const [selectedStoreId, setSelectedStoreId] = useState('');
    const [selectedChannel, setSelectedChannel] = useState(WHATSAPP_CHANNEL);
    const [selectedArea, setSelectedArea] = useState('');
    const [isTemplatesOpen, setIsTemplatesOpen] = useState(false);
    const [loading, setLoading] = useState(false);
    const [currentTemplate, setCurrentTemplate] =
        useState<TCurrentTemplate>(CURRENT_TEMPLATE_DEFAULT);

    const recipientFormik = useFormik({
        initialValues: {
            phone: '',
            countryCode: ECountryCode.Mexico,
            name: '',
        },
        onSubmit: () => {},
    });

    const isDeveloper = user?.accessLevel === DEVELOPER_LEVEL_ACCESS;

    useEffect(() => {
        if (!open) return;

        setSelectedStoreId('');
        setSelectedChannel(WHATSAPP_CHANNEL);
        setSelectedArea('');
        recipientFormik.resetForm();
        setIsTemplatesOpen(false);
        setCurrentTemplate(CURRENT_TEMPLATE_DEFAULT);

        if (isDeveloper) getStores();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open]);

    useEffect(() => {
        if (!open || !selectedStoreId) return;
        getStore(selectedStoreId);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, selectedStoreId]);

    const accessibleStores = useMemo(
        () => getAccessibleStoresForUser(user, stores),
        [user, stores]
    );

    const storeOptions = useMemo(
        () => mapStores(accessibleStores as any),
        [accessibleStores]
    );

    const selectedStore = useMemo(() => {
        if (selectedStoreId && store && getDocumentId(store) === selectedStoreId) {
            return store;
        }

        return (
            accessibleStores.find(
                (item: any) => getDocumentId(item) === selectedStoreId
            ) || null
        );
    }, [accessibleStores, selectedStoreId, store]);

    const areaOptions = useMemo(() => {
        const areas = getWhatsappAreas(
            selectedStore?.areas || [],
            user?.areas || []
        );

        return areas.map((item) => ({
            value: item.name,
            label: item.name,
        }));
    }, [selectedStore?.areas, user?.areas]);

    useEffect(() => {
        if (!areaOptions.length) {
            setSelectedArea('');
            return;
        }

        if (!areaOptions.some((option) => option.value === selectedArea)) {
            setSelectedArea(areaOptions[0].value);
        }
    }, [areaOptions, selectedArea]);

    const normalizedPhone = getContactWhatsAppPhone(
        recipientFormik.values.phone,
        recipientFormik.values.countryCode
    );
    const recipientName = recipientFormik.values.name;

    const conversation = useMemo((): ICurrentConversation | undefined => {
        if (
            !selectedStore?._id ||
            !selectedArea ||
            !normalizedPhone ||
            selectedChannel !== WHATSAPP_CHANNEL
        ) {
            return undefined;
        }

        const outboundConversationId = buildOutboundConversationId(
            selectedStore._id,
            selectedArea,
            normalizedPhone
        );

        // ModalTemplates applies createFromConversation to this base config.
        return ConversationFactory.createBaseConfig(
            outboundConversationId,
            recipientName.trim() || normalizedPhone,
            normalizedPhone,
            selectedArea as TAreas,
            selectedStore
        );
    }, [
        normalizedPhone,
        recipientName,
        recipientFormik.values.countryCode,
        recipientFormik.values.phone,
        selectedArea,
        selectedChannel,
        selectedStore,
    ]);

    const handleClose = () => {
        setOpen(false);
        setIsTemplatesOpen(false);
    };

    const handleContinueToTemplates = () => {
        if (!selectedStoreId) {
            enqueueSnackbar(t('Chat.SelectStore'), { variant: 'warning' });
            return;
        }

        if (!selectedArea) {
            enqueueSnackbar(t('Chat.SelectArea'), { variant: 'warning' });
            return;
        }

        if (!normalizedPhone) {
            enqueueSnackbar(t('Errors.NoValidPhone'), { variant: 'warning' });
            return;
        }

        setIsTemplatesOpen(true);
    };

    const handleSendTemplate = async (templatePayload: Record<string, any>) => {
        setLoading(true);
        try {
            const { conversationId, ...payload } = templatePayload;
            // ModalTemplates builds the template payload untyped
            const requestPayload = {
                ...payload,
                storeId: selectedStore?._id,
                area: selectedArea,
                recipientPhone: normalizedPhone,
                senderPhone: payload.senderPhone || payload.from,
                ...(isOutboundConversationId(conversationId)
                    ? {}
                    : { conversationId }),
            } as TSendChannelTemplateBody;

            await sendChannelTemplate(requestPayload);
            enqueueSnackbar(t('TwilioTemplates.Send'), { variant: 'success' });
            setIsTemplatesOpen(false);
            handleClose();
            onMessageSent?.();
        } catch {
            enqueueSnackbar(t('Errors.CannotSendWsp'), { variant: 'error' });
        } finally {
            setLoading(false);
        }
    };

    const channelOptions = OUTBOUND_CHANNELS.map((channel) => ({
        value: channel.value,
        label: t(channel.labelKey),
    }));

    const showWhatsappFields = selectedChannel === WHATSAPP_CHANNEL;

    return (
        <>
            {conversation && (
                <ModalTemplates
                    open={isTemplatesOpen}
                    setOpen={setIsTemplatesOpen}
                    conversation={conversation}
                    contextType={TEMPLATE_CONTEXTS_TYPES.CONVERSATION}
                    currentTemplate={currentTemplate}
                    setCurrentTemplate={setCurrentTemplate}
                    handleSendTemplate={handleSendTemplate}
                    loading={loading}
                    contextKey={`${selectedStoreId}-${selectedArea}-${normalizedPhone}`}
                />
            )}

            <EnchancedModal
                title={t('Chat.OutboundMessageTitle')}
                open={open}
                setOpen={setOpen}
                maxWidth="sm"
            >
                <Box
                    display="flex"
                    flexDirection="column"
                    style={{ gap: 16 }}
                >
                    <Box
                        display="flex"
                        alignItems="center"
                        style={{ gap: 12 }}
                    >
                        <SelectFilter
                            label={t('contacts.agency')}
                            value={selectedStoreId}
                            onChange={(event) => {
                                setSelectedStoreId(event.target.value);
                                setSelectedArea('');
                            }}
                            options={[
                                { value: '', label: t('Chat.SelectStore') },
                                ...storeOptions,
                            ]}
                        />
                    </Box>

                    <Box display="flex" alignItems="center" style={{ gap: 12 }}>
                        <SelectFilter
                            label={t('Chat.SelectChannel')}
                            value={selectedChannel}
                            onChange={(event) =>
                                setSelectedChannel(event.target.value)
                            }
                            options={channelOptions}
                        />
                    </Box>

                    {showWhatsappFields && (
                        <Box
                            display="flex"
                            flexDirection="column"
                            style={{ gap: 16 }}
                        >
                            <Typography variant="body2" color="textSecondary">
                                {t('Chat.OutboundWhatsappHint')}
                            </Typography>

                            <Box display="flex" alignItems="center" style={{ gap: 12 }}>
                                <SelectFilter
                                    label={t('Chat.SelectArea')}
                                    value={selectedArea}
                                    onChange={(event) =>
                                        setSelectedArea(event.target.value)
                                    }
                                    options={[
                                        {
                                            value: '',
                                            label: t('Chat.SelectArea'),
                                        },
                                        ...areaOptions,
                                    ]}
                                    disabled={!selectedStoreId}
                                />
                            </Box>

                            <AreaCodeTextField
                                name="phone"
                                values={recipientFormik.values}
                                setFieldValue={recipientFormik.setFieldValue}
                                touched={recipientFormik.touched}
                                errors={recipientFormik.errors}
                                handleChange={recipientFormik.handleChange}
                                props={{
                                    variant: 'outlined',
                                    fullWidth: true,
                                    label: t('Chat.RecipientPhone'),
                                    required: true,
                                }}
                            />

                            <TextField
                                variant="outlined"
                                fullWidth
                                label={t('Chat.RecipientName')}
                                name="name"
                                value={recipientFormik.values.name}
                                onChange={recipientFormik.handleChange}
                                onBlur={recipientFormik.handleBlur}
                            />

                            <Button
                                variant="contained"
                                color="primary"
                                onClick={handleContinueToTemplates}
                                disabled={
                                    !selectedStoreId ||
                                    !selectedArea ||
                                    !normalizedPhone
                                }
                            >
                                {t('Chat.ContinueToTemplates')}
                            </Button>
                        </Box>
                    )}
                </Box>
            </EnchancedModal>
        </>
    );
};
